import { action, computed, observable, runInAction } from 'mobx'
import { type Job, type RatingMethod } from '@/features/jobs/Job'
import { type RatingStatus } from './ratings-mappers'
import { RateProDialogViewModel } from '@/features/jobs/details/ratings/components/RateProDialog/RateProDialogViewModel'
import { type RatingsStore } from './RatingsStore'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { distinctUntilChanged, filter, merge, switchMap, takeWhile } from 'rxjs'
import { type JobProgressionViewModel } from '@/features/jobs/details/components/JobProgressionCard/JobProgressionViewModel'
import { tapAction, toStream } from '@/utils/mobx-util'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { withAbortSignal } from '@/utils/rx-util'

export class RatingsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @observable
  private accessor _proRatingStatus: RatingStatus = 'Unknown'

  readonly rateProDialogVm

  constructor(
    private readonly _job: Job,
    private readonly _canTransferAsAdmin: () => boolean,
    private readonly _ratingsStore: RatingsStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _jobProgressionVm: JobProgressionViewModel,
  ) {
    this.rateProDialogVm = new RateProDialogViewModel(
      () => this.proChampion,
      () => this.ratingMethod,
      (rating: number, feedback: string) =>
        this.submitProRating(rating, feedback),
      this._newAlert,
      this._createAlertErrorHandler,
    )

    this._lifecycle.manage(
      merge(
        toStream(() => this._jobProgressionVm.isCurrentStepStatusDraftApproved),
        toStream(() => this._job.status, true).pipe(distinctUntilChanged()),
      ).pipe(
        switchMap(() =>
          withAbortSignal((signal) =>
            this.fetchJobRatingStatus(signal)
              .then(() => true)
              .catch(() => false),
          ),
        ),
        filter(Boolean),
        // Jobs that are not rated never prompt, so stop refetching for them.
        takeWhile(() => this._proRatingStatus !== 'NotApplicable'),
        filter(() => this.isChampionClient && !this.isAdmin),
        tapAction(() => this.showRateProDialog()),
      ),
    )
  }

  @computed
  get ratingMethod(): RatingMethod {
    return this.isChampionClient
      ? this._job.ratingConfig.customerRatingMethod
      : 'Stars'
  }

  @computed
  get isChampionClient() {
    return this._job.isChampionClientCollaborator
  }

  @computed
  get proChampion() {
    return this._job.proChampion
  }

  @computed
  get canRatePro() {
    return this._proRatingStatus === 'Pending' && !!this.proChampion
  }

  @computed
  get canSeeRatingNotificationBar(): boolean {
    return (
      this._job.isChampionClientCollaborator && this.canRatePro && !this.isAdmin
    )
  }

  @computed
  get isAdmin() {
    return this._canTransferAsAdmin()
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this.rateProDialogVm.modal.close()
    this._lifecycle.deactivate()
  }

  fetchJobRatingStatus(signal?: AbortSignal) {
    return this._createAlertRetryWrapper(
      'Loading the rating status failed',
      signal,
    )(() => this._fetchJobRatingStatus(signal))
  }

  async submitProRating(rating: number, feedback: string) {
    await this._ratingsStore.submitProRating({
      jobId: this._job.id,
      rating,
      proUserId: this._job.proChampion?.member.user.publicId,
      comment: feedback,
    })

    await this._fetchJobRatingStatus()
  }

  @action.bound
  showRateProDialog() {
    if (!this.canRatePro) {
      return
    }

    this.rateProDialogVm.modal.show()
  }

  private async _fetchJobRatingStatus(signal?: AbortSignal) {
    const jobRatingStatus = await this._ratingsStore.fetchJobRatingStatus(
      this._job.id,
      signal,
    )

    runInAction(() => {
      this._proRatingStatus = jobRatingStatus.proRating
    })
  }
}
